import React from "react";
import "./info-detail-cell.scss";

export interface InfoDetailCellItem {
  text: string;
  color?: string;
  /** Max width for this item when truncation is enabled (CSS value, e.g. "120px" or "50%"). */
  maxWidth?: string;
}

export interface InfoDetailCellRow {
  items: InfoDetailCellItem[];
  className?: string;
  /** Color of the dot separators between items in this row. */
  separatorColor?: string;
}

export interface InfoDetailCellAction {
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  /** Color applied to the action icon. */
  iconColor?: string;
}

export interface InfoDetailCellProps {
  title: string;
  titleAction?: InfoDetailCellAction;
  rows?: InfoDetailCellRow[];
  className?: string;
  /** Truncate overflowing text with an ellipsis. Applies to the title and all row items. */
  truncate?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  /** CSS border shorthand (e.g. "1px solid #ccc"). Overrides borderColor and borderWidth when set. */
  border?: string;
  borderRadius?: string;
}

export function InfoDetailCell({
  title,
  titleAction,
  rows = [],
  className = "",
  truncate = false,
  backgroundColor,
  borderColor,
  borderWidth,
  border,
  borderRadius,
}: InfoDetailCellProps) {
  const cellClass = [
    "info-detail-cell",
    truncate && "info-detail-cell--truncate",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const cellStyle: React.CSSProperties = {
    ...(backgroundColor ? { backgroundColor } : undefined),
    ...(border
      ? { border }
      : {
        ...(borderColor ? { borderColor } : undefined),
        ...(borderWidth ? { borderWidth, borderStyle: "solid" } : undefined),
      }),
    ...(borderRadius ? { borderRadius } : undefined),
  };

  const hasStyle = Object.keys(cellStyle).length > 0;
  const titleActionAriaLabel = titleAction?.ariaLabel;
  const titleActionIconColor = titleAction?.iconColor;

  return (
    <div className={cellClass} style={hasStyle ? cellStyle : undefined}>
      <div className="info-detail-cell__header">
        <span className="info-detail-cell__title" title={truncate ? title : undefined}>
          {title}
        </span>
        {titleAction && (
          <button
            type="button"
            className="info-detail-cell__action"
            onClick={(e) => titleAction.onClick(e)}
            aria-label={titleActionAriaLabel ?? "Action"}
            style={titleActionIconColor ? { color: titleActionIconColor } : undefined}
          >
            {titleAction.icon}
          </button>
        )}
      </div>

      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`info-detail-cell__row ${row.className ?? ""}`.trim()}
        >
          {row.items.map((item, itemIndex) => (
            <React.Fragment key={itemIndex}>
              {itemIndex > 0 && (
                <span
                  className="info-detail-cell__separator"
                  aria-hidden="true"
                  style={row.separatorColor ? { backgroundColor: row.separatorColor } : undefined}
                />
              )}
              <span
                className="info-detail-cell__text"
                title={truncate ? item.text : undefined}
                style={{
                  ...(item.color ? { color: item.color } : undefined),
                  ...(truncate && item.maxWidth
                    ? { maxWidth: item.maxWidth }
                    : undefined),
                }}
              >
                {item.text}
              </span>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}
